// The word-cloud branch of a headless dispatcher: config resolution, word processing and the
// cloud itself. Every family that can draw a cloud instead of its own chart (open-ended, the
// answer-variant bars, matrix) reaches the same view through here, as they all reach the same
// open-ended block on the live page.
//
// A component rather than a helper so useWordProcessor stays an unconditional hook call in
// dispatchers that only sometimes draw a cloud.

import { useMemo } from 'react'
import PropTypes from 'prop-types'

import { useWordProcessor } from '@aytm/app/stats/page/question-views/components/word-cloud/hooks/useWordProcessor'
import { withDefaults } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/config'
import { applyPreset } from '@aytm/app/stats/page/question-views/components/word-cloud/utils/applyPreset'

import WordCloudChart from '@aytm/app/stats/page/question-views/components/word-cloud/Chart'

// The cloud's own canvas ratio, orientation-driven, off the page width the view is given.
const heightFor = (orientation, width) => Math.round((('v' === orientation ? 855 : 426) * width) / 662)

const HeadlessWordCloud = ({ id, questionKey, rawData, wcConfig, rejects, width, height, useRawData }) => {
  // useWordProcessor destructures config WITHOUT per-field defaults (its `activeWord === ''`
  // check misroutes into active-word mode on undefined) — the live view always feeds it a
  // preset-complete config, so mirror that. Rejects answer for their fields outright, over
  // the caller's config AND the preset, as they do on the live page.
  const config = useMemo(
    () => withDefaults({ ...applyPreset(wcConfig, rawData?.words?.length || 0), ...rejects }),
    [rejects, wcConfig, rawData]
  )

  const { words } = useWordProcessor(rawData, config, useRawData)

  return (
    <WordCloudChart
      width={width}
      height={height || heightFor(config.orientation, width)}
      model={{ id, key: questionKey, useRawData }}
      words={words}
      config={config}
      scope={[]}
      disabled={false}
      onParamsChange={undefined}
      onWordClick={undefined}
    />
  )
}

HeadlessWordCloud.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  rawData: PropTypes.object.isRequired,
  wcConfig: PropTypes.object,
  // Fields the host view fixes regardless of what the config says.
  rejects: PropTypes.object,
  width: PropTypes.number.isRequired,
  height: PropTypes.number,
  useRawData: PropTypes.bool
}

HeadlessWordCloud.defaultProps = {
  wcConfig: {},
  rejects: {}
}

HeadlessWordCloud.displayName = 'HeadlessWordCloud'

export default HeadlessWordCloud
